import React from "react";
import {
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import produce from "immer";
import { initialState } from "./initialState";

export const subjectiveRefractionHeaders = [
  "Subjective Refraction",
  "SPH",
  "CYL",
  "Axis",
  "DVA",
  "Add",
  "NVA",
  "IPD"
];

interface SubjectiveRefractionProps {
  visionData: typeof initialState;
  onVisionChange: (updatedVision: typeof initialState) => void;
}

const ROWS = ["OD", "OS"] as const;
const COLS = [
  { key: "sph", label: "SPH" },
  { key: "cyl", label: "CYL" },
  { key: "axis", label: "Axis" },
  { key: "dva", label: "DVA" },
  { key: "add", label: "Add" },
  { key: "nva", label: "NVA" },
  { key: "ipd", label: "IPD" }
] as const;

export default function SubjectiveRefraction({
  visionData,
  onVisionChange
}: SubjectiveRefractionProps): React.JSX.Element {
  const handleChange = (
    row: (typeof ROWS)[number],
    col: (typeof COLS)[number]["key"],
    value: string
  ) => {
    const updated = produce(visionData, (draft: typeof initialState) => {
      if (!draft.vision) {
        draft.vision = JSON.parse(JSON.stringify(initialState.vision));
      }
      if (!draft.vision.subjectiveRefraction) {
        draft.vision.subjectiveRefraction = JSON.parse(
          JSON.stringify(initialState.vision.subjectiveRefraction)
        );
      }
      if (!draft.vision.subjectiveRefraction[row]) {
        draft.vision.subjectiveRefraction[row] = JSON.parse(
          JSON.stringify(initialState.vision.subjectiveRefraction[row] || {})
        );
      }
      const { subjectiveRefraction } = draft.vision;
      const eyeData = subjectiveRefraction?.[row] as unknown as Record<string, string | null>;
      if (eyeData) {
        eyeData[col] = value === "" ? null : value;
      }
    });
    onVisionChange(updated);
  };

  const currentSubjectiveRefraction = visionData?.vision?.subjectiveRefraction || {};

  const cellStyle = {
    border: 1,
    borderColor: "divider"
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    textAlign: "center" as const
  };

  return (
    <TableContainer>
      <Typography
        sx={{
          padding: 1,
          textAlign: "center",
          backgroundColor: "#ededed",
          borderLeft: 1,
          borderRight: 1,
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        Subjective Refraction
      </Typography>

      <Table size="small" sx={{ border: 1, borderColor: "divider", borderRadius: "5px" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellStyle, width: "100px" }} />
            {COLS.map((col) => (
              <TableCell key={col.key} sx={headerCellStyle}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ROWS.map((row) => (
            <TableRow key={row}>
              <TableCell sx={{ ...headerCellStyle, textAlign: "left" }}>{row}</TableCell>
              {COLS.map((col) => (
                <TableCell key={col.key} sx={cellStyle}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={currentSubjectiveRefraction[row]?.[col.key] ?? ""}
                    onChange={(e) => handleChange(row, col.key, e.target.value)}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "4px 8px",
                        fontSize: "0.875rem"
                      }
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
